import { api } from './api';

export type PersonalLibraryType = 'recipe' | 'photo' | 'playlist' | 'note' | 'task' | 'link';

export interface PersonalItem {
  id: string;
  type: PersonalLibraryType;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  meta?: {
    chips?: string[];
    [key: string]: unknown;
  } | null;
  data?: any;
  createdAt: string;
  updatedAt: string;
}

export type PersonalItemComment = {
  id: string;
  author: string;
  authorId?: string;
  text: string;
  createdAt: string;
};

export type PersonalItemEngagement = {
  comments: PersonalItemComment[];
  reactions: Record<string, { id: string; name: string; createdAt: string }[]>;
};

export interface ChannelSharedItem {
  id: string;
  channelId: string;
  personalItemId: string;
  sharedByUserId: string;
  visibility: 'full' | 'summary';
  createdAt: string;
  item: PersonalItem;
}

export type LibraryVisibility = 'public' | 'friends' | 'private';
export type LibraryPrivacyMap = Record<PersonalLibraryType, LibraryVisibility>;

export async function fetchPersonalItems(token: string, type?: PersonalLibraryType, userId?: string): Promise<PersonalItem[]> {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (userId) params.set('userId', userId);
  const query = params.toString();
  const res = await api.getAuth(`/personal-items${query ? `?${query}` : ''}`, token);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function fetchPersonalItemEngagement(
  token: string,
  ids: string[]
): Promise<Record<string, PersonalItemEngagement>> {
  if (!ids || ids.length === 0) return {};
  const deduped = Array.from(new Set(ids.filter(Boolean)));
  const query = deduped.join(',');
  if (!query) return {};
  const res = await api.getAuth(`/personal-items/engagement?ids=${encodeURIComponent(query)}`, token);
  return res?.engagement || {};
}

export async function createPersonalItem(
  token: string,
  payload: { type: PersonalLibraryType; title: string; description?: string | null; data?: any }
): Promise<PersonalItem> {
  const res = await api.postAuth('/personal-items', payload, token);
  return res?.item;
}

export async function updatePersonalItem(
  token: string,
  id: string,
  updates: Partial<Pick<PersonalItem, 'title' | 'description' | 'data' | 'type'>>
): Promise<PersonalItem> {
  if (!id) throw new Error('Missing personal item id');
  const res = await api.putAuth(`/personal-items/${encodeURIComponent(id)}`, updates, token);
  return res?.item;
}

export async function deletePersonalItem(token: string, id: string): Promise<void> {
  await api.deleteAuth(`/personal-items/${id}`, {}, token);
}

export async function addPersonalItemComment(token: string, personalItemId: string, text: string): Promise<PersonalItemComment> {
  const res = await api.postAuth(`/personal-items/${encodeURIComponent(personalItemId)}/comments`, { text }, token);
  return res?.comment;
}

export async function deletePersonalItemComment(token: string, personalItemId: string, commentId: string): Promise<void> {
  await api.deleteAuth(
    `/personal-items/${encodeURIComponent(personalItemId)}/comments/${encodeURIComponent(commentId)}`,
    {},
    token
  );
}

export async function togglePersonalItemReaction(
  token: string,
  personalItemId: string,
  emoji: string
): Promise<PersonalItemEngagement> {
  const res = await api.postAuth(`/personal-items/${encodeURIComponent(personalItemId)}/reactions`, { emoji }, token);
  return res?.engagement;
}

export async function fetchChannelSharedItems(token: string, channelId: string): Promise<ChannelSharedItem[]> {
  const res = await api.getAuth(`/channels/${encodeURIComponent(channelId)}/shared-items`, token);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function fetchLibraryPrivacy(token: string): Promise<LibraryPrivacyMap> {
  const res = await api.getAuth('/personal-library-privacy', token);
  const map: LibraryPrivacyMap = {
    recipe: 'private',
    photo: 'private',
    playlist: 'private',
    note: 'private',
    task: 'private',
    link: 'private',
  };
  if (res?.privacy) {
    for (const key of Object.keys(map)) {
      const v = res.privacy[key as PersonalLibraryType];
      if (v === 'public' || v === 'friends' || v === 'private') {
        map[key as PersonalLibraryType] = v;
      }
    }
  }
  return map;
}

export async function updateLibraryPrivacy(
  token: string,
  type: PersonalLibraryType,
  visibility: LibraryVisibility
): Promise<void> {
  await api.postAuth('/personal-library-privacy', { type, visibility }, token);
}

function normalizeStringList(val: unknown): string[] {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  if (typeof val === 'string') {
    return val
      .split(/\r?\n|,/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

export type RecipeDetails = {
  image?: string;
  description?: string;
  ingredients: string[];
  servings?: string;
  prepTime?: string;
  cookTime?: string;
  totalTime?: string;
  tags: string[];
  sourceUrl?: string;
};

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

export function extractRecipeDetails(item: PersonalItem): RecipeDetails {
  const data = item?.data;
  const safe = data && typeof data === 'object' ? (data as Record<string, any>) : {};
  const imageField = [safe.image, safe.photo, safe.cover, safe.thumbnail, safe.banner, safe.hero].find(
    (value) => typeof value === 'string' && value.trim().length > 0
  );
  const description =
    (item.description || '').trim() ||
    (typeof safe.description === 'string' ? safe.description.trim() : '') ||
    (typeof safe.summary === 'string' ? safe.summary.trim() : '') ||
    (typeof safe.note === 'string' ? safe.note.trim() : '');
  const ingredientsSource = safe.ingredients ?? safe.ingredientList ?? safe.items ?? safe.components;
  const ingredients = normalizeStringList(ingredientsSource);
  const tags = normalizeStringList(safe.tags || safe.labels || safe.categories);
  const servings = firstString(safe.servings, safe.yield, safe.portions);
  const prepTime = firstString(safe.prepTime, safe.preparationTime, safe.readyIn);
  const cookTime = firstString(safe.cookTime, safe.cookingTime);
  const totalTime = firstString(safe.totalTime, safe.duration);
  const sourceUrl = firstString(safe.url, safe.source, safe.link);

  return {
    image: imageField ? String(imageField).trim() : undefined,
    description: description || undefined,
    ingredients,
    servings,
    prepTime,
    cookTime,
    totalTime,
    tags,
    sourceUrl,
  };
}
